import React, { useCallback, useEffect, useState } from 'react';
import { Text, Overlay, LoadingBalls } from '@moda/om';
import {
  CartUpdateAction,
  CartUpdatePropertyName,
  useCart,
  useCartItems,
  CartVersion,
  useUpdateCartMutation
} from '../../hooks/useCart';
import { useAddressesQuery, useEditAddressMutation } from '../../hooks/useAddress';
import { tc } from '../../lib/trackingContext';
import { reportError } from '../../lib/reportError';
import { useErrorModal } from '../../hooks/useErrorModal';
import { useCheckoutFormStates } from '../CheckoutFormStates';
import { CheckoutSection } from '../CheckoutSection';
import { ShippingMethodForm, SameDayData } from './ShippingMethodForm';
import { ShippingMethodPreview } from './ShippingMethodPreview';

enum Mode {
  Resting,
  Editing,
  Saving
}

export const ShippingMethod: React.FC = () => {
  const { cart } = useCart();
  const { availableNowItems } = useCartItems();
  const [updateCart] = useUpdateCartMutation();
  const [editAddress] = useEditAddressMutation();
  const { addresses } = useAddressesQuery();
  const { setFormState, sameDayDeliveryNotes, setSameDayDeliveryNotes } = useCheckoutFormStates();
  const { showErrorModal } = useErrorModal();

  const { selectedShippingMethodId, shippingMethods, addressId } = cart.attributes;

  const currentAddress = addresses.find(addr => addr.id === String(addressId));

  const selectedShippingMethod = shippingMethods?.find(
    method => method.method === selectedShippingMethodId
  );
  const onlyOneShippingMethod = shippingMethods?.length === 1;

  const [mode, setMode] = useState(onlyOneShippingMethod ? Mode.Resting : Mode.Editing);

  const [selectedOptionId, setSelectedOptionId] = useState(selectedShippingMethodId);

  const selectedOption = shippingMethods?.find(method => method.method === selectedOptionId);

  const onSubmit = useCallback(
    async (sameDayData?: SameDayData) => {
      setMode(Mode.Saving);

      try {
        if (sameDayData) {
          const phoneChanged = sameDayData.phone !== currentAddress?.attributes.phone;
          if (phoneChanged && currentAddress) {
            await editAddress({
              ...currentAddress,
              attributes: {
                ...currentAddress.attributes,
                phone: sameDayData.phone
              }
            });
          }
          // Store delivery notes in context for order placement
          setSameDayDeliveryNotes(sameDayData.sameDayDeliveryNotes);
        } else {
          // Clear delivery notes if not same-day delivery
          setSameDayDeliveryNotes('');
        }

        await updateCart(CartUpdateAction.ShippingMethod, {
          [CartUpdatePropertyName.ShippingMethod]: selectedOptionId ?? undefined
        });
        setMode(Mode.Resting);
      } catch (error) {
        setMode(Mode.Editing);
        showErrorModal();
        reportError('SHIPPING_METHOD_SUBMIT', error);
      }
    },
    [
      updateCart,
      editAddress,
      currentAddress,
      selectedOptionId,
      showErrorModal,
      setSameDayDeliveryNotes
    ]
  );

  const trackShippingMethod = useCallback(() => {
    if (selectedOption) {
      availableNowItems.forEach(lineItem =>
        tc.track('Product Delivery Timing Shown', {
          addToScopeOnce: { lineItem },
          addToTrackingEvent: { label: selectedOption.transitTime }
        })
      );
    }
  }, [availableNowItems, selectedOption]);

  useEffect(() => {
    setFormState('shippingMethod', mode === Mode.Resting ? 'closed' : 'open');
  }, [setFormState, mode]);

  if (cart.attributes.version !== CartVersion.Checkout) return null;

  if (shippingMethods == null || selectedOptionId == null) return null;

  return (
    <CheckoutSection title="Shipping Method">
      {(mode === Mode.Editing || mode === Mode.Saving) && (
        <ShippingMethodForm
          shippingOptions={shippingMethods}
          selectedOption={selectedOptionId}
          onSelectOption={setSelectedOptionId}
          onCancel={() => setMode(Mode.Resting)}
          onSubmit={(_method, sameDayData) => {
            onSubmit(sameDayData);
            trackShippingMethod();
          }}
          initialPhone={currentAddress?.attributes.phone || ''}
          initialDeliveryNotes={sameDayDeliveryNotes}
        />
      )}
      {mode === Mode.Resting && (
        <ShippingMethodPreview
          option={selectedShippingMethod}
          onChange={shippingMethods.length > 1 ? () => setMode(Mode.Editing) : undefined}
          phone={currentAddress?.attributes.phone}
          deliveryNotes={sameDayDeliveryNotes}
        />
      )}
      <Overlay show={mode === Mode.Saving}>
        <LoadingBalls />

        <Text treatment="eyebrow">Saving</Text>
      </Overlay>
    </CheckoutSection>
  );
};
